import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ChevronRight, Lightbulb, AlertTriangle, User } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { getGuideBySlug, GUIDES } from "../lib/data";
import { Breadcrumbs } from "../components/Breadcrumbs";
import {
    SEO,
    articleSchema,
    breadcrumbSchema,
    faqSchema,
} from "../components/SEO";

const GuideDetail = () => {
    const { slug } = useParams();
    const g = getGuideBySlug(slug);
    const [active, setActive] = useState("introduction");

    if (!g) return <Navigate to="/guides" replace />;

    const TOC = [
        { id: "introduction", label: "Introduction" },
        ...(g.sections || []).map(s => ({ id: s.id, label: s.title })),
        { id: "faq", label: "Questions fréquentes" }
    ];

    const faqs = g.faqs || [];

    return (
        <div data-testid={`guide-detail-${slug}`}>
            <SEO
                title={`${g.title} | Pronospot.com`}
                description={g.excerpt}
                jsonLd={[
                    articleSchema({
                        title: g.title,
                        description: g.excerpt,
                        url: `https://pronospot.com/guides/${g.slug}`,
                    }),
                    breadcrumbSchema([
                        { name: "Accueil", url: "https://pronospot.com/" },
                        { name: "Guides", url: "https://pronospot.com/guides" },
                        {
                            name: g.title,
                            url: `https://pronospot.com/guides/${g.slug}`,
                        },
                    ]),
                    faqSchema(faqs),
                ]}
            />
            <section className="container-x pt-10 pb-6">
                <Breadcrumbs
                    items={[
                        { label: "Guides", to: "/guides" },
                        { label: g.title },
                    ]}
                />
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <span className="chip">{g.category}</span>
                    <span className="text-xs text-gray-500">
                        {g.readTime} de lecture
                    </span>
                </div>
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tighter max-w-3xl">
                    {g.title}
                </h1>
                <p className="text-gray-400 mt-4 max-w-2xl text-lg">
                    {g.excerpt}
                </p>
            </section>

            <section className="container-x pb-20">
                <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
                    {/* TOC */}
                    <aside className="hidden lg:block">
                        <div className="sticky top-24">
                            <h4 className="font-heading font-bold text-white text-xs uppercase tracking-wider mb-4">
                                Sommaire
                            </h4>
                            <ul className="space-y-2 text-sm border-l border-[#2A4A6B] pl-4">
                                {TOC.map((t) => (
                                    <li key={t.id}>
                                        <a
                                            href={`#${t.id}`}
                                            onClick={() => setActive(t.id)}
                                            className={`block py-1 transition-colors ${
                                                active === t.id
                                                    ? "text-[#00C853] font-semibold"
                                                    : "text-gray-400 hover:text-slate-900 dark:hover:text-white"
                                            }`}
                                        >
                                            {t.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </aside>

                    <article className="prose-custom max-w-3xl space-y-8">
                        <div id="introduction" className="scroll-mt-24">
                            <h2 className="font-heading text-2xl font-bold text-white mb-3">
                                Introduction
                            </h2>
                            <p className="text-gray-300 leading-relaxed">
                                {g.introduction}
                            </p>
                        </div>

                        {g.sections && g.sections.map((s) => (
                            <div key={s.id} id={s.id} className="scroll-mt-24">
                                <h2 className="font-heading text-2xl font-bold text-white mb-3">
                                    {s.title}
                                </h2>
                                <p className="text-gray-300 leading-relaxed mb-4">
                                    {s.content}
                                </p>
                                {s.id === "choisir-bookmaker" && (
                                    <div className="card-elev p-5 border-l-4 border-l-[#00C853] my-4">
                                        <div className="flex gap-3">
                                            <Lightbulb className="text-[#00C853] flex-shrink-0" size={20} />
                                            <div>
                                                <strong className="text-white block mb-1">Conseil Pronospot</strong>
                                                <p className="text-sm text-gray-300">
                                                    Commencez par 2 ou 3 bookmakers maximum afin de comparer les cotes et profiter de plusieurs bonus de bienvenue.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {s.id === "erreurs" && (
                                    <div className="card-elev p-5 border-l-4 border-l-[#FFB300] my-4">
                                        <div className="flex gap-3">
                                            <AlertTriangle className="text-[#FFB300] flex-shrink-0" size={20} />
                                            <div>
                                                <strong className="text-white block mb-1">Attention</strong>
                                                <p className="text-sm text-gray-300">
                                                    Ne courez jamais après vos pertes en doublant vos mises. C'est le piège classique qui mène à l'addiction et aux problèmes financiers.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}

                        {faqs.length > 0 && (
                            <div id="faq" className="scroll-mt-24">
                                <h2 className="font-heading text-2xl font-bold text-white mb-5">
                                    Questions fréquentes
                                </h2>
                                <Accordion type="single" collapsible className="space-y-3">
                                    {faqs.map((f, i) => (
                                        <AccordionItem
                                            key={i}
                                            value={`item-${i}`}
                                            className="card-elev px-5 border-none"
                                            data-testid={`faq-item-${i}`}
                                        >
                                            <AccordionTrigger className="text-white font-heading font-semibold hover:no-underline">
                                                {f.q}
                                            </AccordionTrigger>
                                            <AccordionContent className="text-gray-400">
                                                {f.a}
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </div>
                        )}

                        {/* Author box */}
                        <div className="card-elev p-6 flex items-center gap-4 mt-12">
                            <div className="w-14 h-14 rounded-full bg-[#1E3A5F] border border-[#2A4A6B] flex items-center justify-center">
                                <User size={26} className="text-[#00C853]" />
                            </div>
                            <div>
                                <div className="text-xs text-gray-400 uppercase tracking-wider">
                                    Écrit par
                                </div>
                                <div className="font-heading font-bold text-white">
                                    Équipe Pronospot
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    Rédacteurs spécialisés paris sportifs depuis 2018
                                </div>
                            </div>
                        </div>

                        {/* Related guides */}
                        <div className="mt-12">
                            <h3 className="font-heading text-xl font-bold text-white mb-5">
                                Guides liés
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {GUIDES.filter((x) => x.slug !== slug)
                                    .slice(0, 2)
                                    .map((rg) => (
                                        <Link
                                            key={rg.slug}
                                            to={`/guides/${rg.slug}`}
                                            className="card-elev p-5"
                                        >
                                            <span className="chip mb-2 inline-block">
                                                {rg.category}
                                            </span>
                                            <h4 className="font-heading font-bold text-white">
                                                {rg.title}
                                            </h4>
                                            <span className="text-[#00C853] text-sm font-semibold mt-3 inline-flex items-center gap-1">
                                                Lire <ChevronRight size={14} />
                                            </span>
                                        </Link>
                                    ))}
                            </div>
                        </div>
                    </article>
                </div>
            </section>
        </div>
    );
};

export default GuideDetail;
